import { useCallback, useEffect, useMemo, useState } from 'react'
import LedgerScannerClient from '../clients/LedgerScannerClient'
import { PriceQuote, TimeFrame } from '../interfaces/tokenPriceInfo'
import { logger } from '../logger'
import useConfig from './useConfig'

export interface Range {
  value: number
  label: string
  frame: TimeFrame
  dateFormat: string
}

interface Props {
  tokenId?: string
  range: Range
}

const useTokenPriceInfo = ({ tokenId, range }: Props) => {
  const [priceQuotes, setPriceQuotes] = useState<PriceQuote[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const { tokenPricesWithChange } = useConfig()

  const fetchQuotes = useCallback(async () => {
    if (!tokenId) return

    setIsLoading(true)

    try {
      const quotes = await LedgerScannerClient.fetchTokenPriceHistory(tokenId)
      setPriceQuotes(quotes)
    } catch (e) {
      logger.debug('Failed to load token analytics', { originalError: e })
    } finally {
      setIsLoading(false)
    }
  }, [tokenId, range.value, range.frame, range.label])

  useEffect(() => {
    fetchQuotes()
  }, [tokenId])

  const filteredQuotes = useMemo(() => {
    const now = new Date()
    switch (range.label) {
      case '1D':
        now.setHours(now.getHours() - 24)
        break
      case '1W':
        now.setDate(now.getDate() - 7)
        break
      case '1M':
        now.setMonth(now.getMonth() - 1)
    }

    const startTimestamp = Math.floor(now.getTime())
    return priceQuotes.filter((q) => q.timestamp >= startTimestamp)
  }, [priceQuotes, range.label])

  return {
    priceQuotes: filteredQuotes,
    price: tokenPricesWithChange[tokenId || '']?.price,
    isLoading,
  }
}

export default useTokenPriceInfo
